import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { listExperiences, type ExperienceDto } from '@/lib/api/core';

/**
 * useExperiences — catálogo de Experiencias VIP (`GET /v1/experiences` de
 * api-core). El endpoint quedó backfilleado (cierre de GAP-07): expone título,
 * categoría, precio (en centavos), ubicación, duración, cupo y highlights.
 *
 * Nota precios: el server devuelve `price_cents` en centavos; `formatCOP`
 * espera pesos enteros (COP zero-decimal), así que normalizamos ÷100.
 */

export type ExperienceCategory = 'cena' | 'viaje' | 'encuentro';

export const EXPERIENCE_CATEGORY_LABELS: Record<ExperienceCategory, string> = {
  cena: 'Cena',
  viaje: 'Viaje',
  encuentro: 'Encuentro',
};

const CATEGORY_EMOJI: Record<string, string> = {
  cena: '🍷',
  viaje: '✈️',
  encuentro: '🤝',
};

/** Emoji representativo de una categoría de experiencia (placeholder visual). */
export function experienceEmoji(category: string): string {
  return CATEGORY_EMOJI[category] ?? '✨';
}

export type Experience = {
  id: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  priceCents: number;
  category: string;
  location: string | null;
  duration: string | null;
  maxCapacity: number | null;
  imageUrl: string | null;
  highlights: string[];
};

/** Centavos (minor units del server) → pesos COP enteros (lo que espera `formatCOP`). */
function centsToWholeCop(value: unknown): number {
  if (value === null || value === undefined || value === '') return 0;
  const n = typeof value === 'string' ? Number.parseFloat(value) : Number(value);
  return Number.isFinite(n) ? Math.round(n / 100) : 0;
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function mapExperience(r: ExperienceDto): Experience {
  return {
    id: String(r.id),
    title: str(r.title) ?? 'Experiencia',
    subtitle: str(r.subtitle),
    description: str(r.description),
    priceCents: centsToWholeCop(r.price_cents),
    category: str(r.category) ?? 'encuentro',
    location: str(r.location),
    duration: str(r.duration),
    maxCapacity:
      typeof r.max_capacity === 'number' && Number.isFinite(r.max_capacity) ? r.max_capacity : null,
    imageUrl: str(r.image_url),
    highlights: Array.isArray(r.highlights)
      ? r.highlights.filter((h): h is string => typeof h === 'string' && h.trim().length > 0)
      : [],
  };
}

async function fetchExperiences(): Promise<Experience[]> {
  const data = await listExperiences();
  return data.map(mapExperience);
}

export function useExperiences(): UseQueryResult<Experience[]> {
  return useQuery({
    queryKey: ['experiences', 'active'],
    queryFn: fetchExperiences,
    staleTime: 5 * 60_000,
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | null)?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
  });
}
